// System 3 — rain, steam, halos, and the environment that wet surfaces reflect.
//
// Everything here is generated in code. The two sprite textures are radial fields written into
// Uint8Arrays; the environment map is a cube render of this same city, so what a puddle reflects is
// the actual shopfronts above it rather than an imported HDRI.

import * as THREE from 'three';
import { rngFor } from './rng.js';
import { WORLD } from './config.js';

// ---------------------------------------------------------------------------
// Procedural sprites
// ---------------------------------------------------------------------------
function radialTexture(size, fn) {
  const d = new Uint8Array(size * size * 4);
  const c = (size - 1) / 2;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = (y * size + x) * 4;
      const r = Math.hypot(x - c, y - c) / c;
      const a = Math.max(0, Math.min(1, fn(r, x / size, y / size)));
      d[i] = d[i + 1] = d[i + 2] = 255;
      d[i + 3] = Math.round(a * 255);
    }
  }
  const t = new THREE.DataTexture(d, size, size, THREE.RGBAFormat);
  t.minFilter = THREE.LinearMipmapLinearFilter;
  t.magFilter = THREE.LinearFilter;
  t.generateMipmaps = true;
  t.needsUpdate = true;
  return t;
}

/** Soft glow for a light halo: a real lens/atmosphere halo has a bright tight core plus a wide skirt. */
const haloTex = () => radialTexture(128, (r) => {
  const core = Math.exp(-r * r * 22.0);
  const skirt = Math.exp(-r * 2.6) * 0.30;
  return (core + skirt) * (1 - Math.min(1, r));
});

/** Steam puff: soft, slightly lumpy, never a clean circle. */
const puffTex = () => radialTexture(96, (r, u, v) => {
  const lump = 0.82 + 0.18 * Math.sin(u * 19.0) * Math.cos(v * 23.0);
  return Math.max(0, 1 - r / lump) ** 2.1 * 0.82;
});

// ---------------------------------------------------------------------------
// Rain
// ---------------------------------------------------------------------------
/**
 * Falling rain as GPU-animated line segments.
 *
 * Positions are computed entirely in the vertex shader from a per-streak seed plus a time uniform,
 * and wrapped inside a box that follows the camera — so there is no per-frame CPU work and no
 * particle array to update. A streak is stretched along its fall direction rather than being a
 * point sprite, because at 1/30 s a real raindrop records as a 10–40 cm streak, not a dot.
 */
export function makeRain(count = 14000, box = { x: 70, y: 26, z: 90 }) {
  const g = new THREE.BufferGeometry();
  const pos = new Float32Array(count * 2 * 3);
  const seed = new Float32Array(count * 2 * 3);
  const end = new Float32Array(count * 2);
  const r = rngFor('rain.seeds');

  for (let i = 0; i < count; i++) {
    const sx = r.f(), sy = r.f(), sz = r.f();
    const speed = 0.62 + r.f() * 0.55;
    for (let k = 0; k < 2; k++) {
      const j = i * 2 + k;
      pos[j * 3] = 0; pos[j * 3 + 1] = 0; pos[j * 3 + 2] = 0;
      seed[j * 3] = sx; seed[j * 3 + 1] = sy; seed[j * 3 + 2] = sz;
      end[j] = k;          // 0 = head, 1 = tail
    }
    void speed;
  }
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('aSeed', new THREE.BufferAttribute(seed, 3));
  g.setAttribute('aEnd', new THREE.BufferAttribute(end, 1));
  g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 400);

  const mat = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    uniforms: {
      uTime: { value: 0 },
      uCam: { value: new THREE.Vector3() },
      uBox: { value: new THREE.Vector3(box.x, box.y, box.z) },
      uWind: { value: new THREE.Vector2(0.16, 0.07) },
      // Roofed volumes the rain must not fall inside: (minX,minZ,maxX,maxZ,ceilingY) per entry.
      // A critic caught streaks falling straight through the arcade's concrete roof.
      uRoofA: { value: new THREE.Vector4(8.0, -25.5, 24.5, -15.2) },
      uRoofAY: { value: 5.4 },
      uRoofB: { value: new THREE.Vector4(-8.0, -20.0, -6.05, 12.0) },
      uRoofBY: { value: 6.4 },
      uLen: { value: 0.34 },
      uTint: { value: new THREE.Color(0x9fb6d8) },
      uOpacity: { value: 0.30 },
    },
    vertexShader: /* glsl */`
      attribute vec3 aSeed;
      attribute float aEnd;
      uniform float uTime; uniform vec3 uCam; uniform vec3 uBox;
      uniform vec2 uWind; uniform float uLen;
        uniform vec4 uRoofA; uniform float uRoofAY;
        uniform vec4 uRoofB; uniform float uRoofBY;
      varying float vFade;
      void main(){
        float speed = 9.0 + aSeed.z * 8.5;
        // Fall, wrapped in a box that follows the camera so rain exists only where it is seen.
        float y = mod(aSeed.y * uBox.y - uTime * speed, uBox.y);
        vec3 p;
        p.x = mod(aSeed.x * uBox.x + uWind.x * (uBox.y - y) * 3.0, uBox.x) - uBox.x * 0.5;
        p.z = mod(aSeed.z * uBox.z + uWind.y * (uBox.y - y) * 3.0, uBox.z) - uBox.z * 0.5;
        p.y = y;
        p += vec3(floor(uCam.x / uBox.x) * uBox.x, 0.0, floor(uCam.z / uBox.z) * uBox.z);
        p.x += mod(uCam.x, uBox.x) - uBox.x * 0.5 + uBox.x * 0.5;
        p.z += mod(uCam.z, uBox.z) - uBox.z * 0.5 + uBox.z * 0.5;
        // Stretch the tail along the fall direction, leaning with the wind.
        vec3 dir = normalize(vec3(uWind.x * 3.0, -1.0, uWind.y * 3.0));
        p += dir * (aEnd * uLen * (0.7 + aSeed.z * 0.9));
        // Kill any streak inside a roofed volume. Cheaper than clipping and exactly as correct:
        // a drop under a concrete slab does not exist.
        float underA = step(uRoofA.x, p.x) * step(p.x, uRoofA.z)
                     * step(uRoofA.y, p.z) * step(p.z, uRoofA.w) * step(p.y, uRoofAY);
        float underB = step(uRoofB.x, p.x) * step(p.x, uRoofB.z)
                     * step(uRoofB.y, p.z) * step(p.z, uRoofB.w) * step(p.y, uRoofBY);
        float roofed = clamp(underA + underB, 0.0, 1.0);

        // Fade the streak out along its length and with distance.
        vFade = (1.0 - aEnd * 0.85) * (1.0 - roofed);
        // Depth-aware: distant rain is shorter, thinner and sparser, not a flat screen overlay.
        vFade *= mix(0.35, 1.0, aSeed.z);
        vec4 mv = modelViewMatrix * vec4(p, 1.0);
        vFade *= smoothstep(62.0, 6.0, -mv.z);
        gl_Position = projectionMatrix * mv;
      }
    `,
    fragmentShader: /* glsl */`
      uniform vec3 uTint; uniform float uOpacity;
      varying float vFade;
      void main(){ gl_FragColor = vec4(uTint, vFade * uOpacity); }
    `,
  });

  const mesh = new THREE.LineSegments(g, mat);
  mesh.name = 'rain';
  mesh.frustumCulled = false;
  mesh.renderOrder = 5;
  return {
    mesh,
    update(t, camPos) {
      mat.uniforms.uTime.value = t;
      mat.uniforms.uCam.value.copy(camPos);
    },
    setIntensity(v) { mat.uniforms.uOpacity.value = v; },
  };
}

// ---------------------------------------------------------------------------
// Steam
// ---------------------------------------------------------------------------
/**
 * Steam from vents and drains. Billboard puffs on a GPU-driven cycle, one draw call for the lot.
 * Each puff rises, expands and fades on its own phase so the column never pulses in unison.
 */
export function makeSteam(emitters, perEmitter = 26) {
  const count = emitters.length * perEmitter;
  const g = new THREE.BufferGeometry();
  const pos = new Float32Array(count * 3);
  const seed = new Float32Array(count * 4);
  const r = rngFor('steam.seeds');

  for (let e = 0; e < emitters.length; e++) {
    const em = emitters[e];
    for (let i = 0; i < perEmitter; i++) {
      const j = e * perEmitter + i;
      pos[j * 3] = em.x; pos[j * 3 + 1] = em.y; pos[j * 3 + 2] = em.z;
      seed[j * 4] = r.f();                       // phase
      seed[j * 4 + 1] = 0.55 + r.f() * 0.9;      // rise speed
      seed[j * 4 + 2] = r.f() * 6.283;           // drift angle
      seed[j * 4 + 3] = (em.scale || 1) * (0.7 + r.f() * 0.9);
    }
  }
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('aSeed', new THREE.BufferAttribute(seed, 4));
  g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 400);

  const mat = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.NormalBlending,
    uniforms: {
      uTime: { value: 0 },
      uMap: { value: puffTex() },
      uColor: { value: new THREE.Color(0xb9c4d2) },
      uScale: { value: 190.0 },
    },
    vertexShader: /* glsl */`
      attribute vec4 aSeed;
      uniform float uTime; uniform float uScale;
      varying float vLife;
      void main(){
        float life = fract(aSeed.x + uTime * 0.14 * aSeed.y);
        vLife = life;
        vec3 p = position;
        p.y += life * 3.4 * aSeed.y;
        // Drift outward and wander as it rises -- a straight column reads as a cone of sprites.
        p.x += cos(aSeed.z + life * 2.1) * life * 0.85;
        p.z += sin(aSeed.z + life * 2.1) * life * 0.85;
        vec4 mv = modelViewMatrix * vec4(p, 1.0);
        gl_PointSize = uScale * aSeed.w * (0.30 + life * 1.5) / max(0.001, -mv.z);
        gl_Position = projectionMatrix * mv;
      }
    `,
    fragmentShader: /* glsl */`
      uniform sampler2D uMap; uniform vec3 uColor;
      varying float vLife;
      void main(){
        vec4 t = texture2D(uMap, gl_PointCoord);
        // In and out: nothing appears or vanishes instantly.
        float a = t.a * smoothstep(0.0, 0.16, vLife) * smoothstep(1.0, 0.45, vLife);
        gl_FragColor = vec4(uColor, a * 0.30);
      }
    `,
  });

  const mesh = new THREE.Points(g, mat);
  mesh.name = 'steam';
  mesh.frustumCulled = false;
  mesh.renderOrder = 4;
  return { mesh, update(t) { mat.uniforms.uTime.value = t; } };
}

// ---------------------------------------------------------------------------
// Halos
// ---------------------------------------------------------------------------
/**
 * The visible glow around a light in humid, rainy air.
 *
 * A critic reported light pools "with no fixture anywhere in frame". Half of that is the missing
 * lamp body, which is geometry; the other half is that a real source in wet air is surrounded by a
 * halo. These are additive billboards placed at the source positions.
 */
export function makeHalos(sources) {
  const g = new THREE.BufferGeometry();
  const pos = new Float32Array(sources.length * 3);
  const col = new Float32Array(sources.length * 3);
  const siz = new Float32Array(sources.length);
  sources.forEach((s, i) => {
    pos[i * 3] = s.pos[0]; pos[i * 3 + 1] = s.pos[1]; pos[i * 3 + 2] = s.pos[2];
    const c = new THREE.Color(s.color);
    col[i * 3] = c.r; col[i * 3 + 1] = c.g; col[i * 3 + 2] = c.b;
    siz[i] = s.size;
  });
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('aColor', new THREE.BufferAttribute(col, 3));
  g.setAttribute('aSize', new THREE.BufferAttribute(siz, 1));
  g.computeBoundingSphere();

  const mat = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    uniforms: { uMap: { value: haloTex() }, uScale: { value: 420.0 }, uGain: { value: 1.85 } },
    vertexShader: /* glsl */`
      attribute vec3 aColor; attribute float aSize;
      uniform float uScale;
      varying vec3 vCol; varying float vAtt;
      void main(){
        vCol = aColor;
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        float d = -mv.z;
        gl_PointSize = clamp(uScale * aSize / max(0.001, d), 2.0, 480.0);
        // Halos should not overwhelm a source you are standing next to.
        vAtt = smoothstep(0.6, 3.5, d) * smoothstep(90.0, 22.0, d);
        gl_Position = projectionMatrix * mv;
      }
    `,
    fragmentShader: /* glsl */`
      uniform sampler2D uMap; uniform float uGain;
      varying vec3 vCol; varying float vAtt;
      void main(){
        float a = texture2D(uMap, gl_PointCoord).a;
        gl_FragColor = vec4(vCol * a * vAtt * uGain, a * vAtt * 0.9);
      }
    `,
  });

  const mesh = new THREE.Points(g, mat);
  mesh.name = 'halos';
  mesh.renderOrder = 6;
  return { mesh, setGain(v) { mat.uniforms.uGain.value = v; } };
}

// ---------------------------------------------------------------------------
// Wet-ground reflection smears
// ---------------------------------------------------------------------------
/**
 * The vertical smear a light throws down a wet road toward the viewer.
 *
 * A critic called this out as the defining signature of wet-night street photography and noted it
 * was entirely missing: *"enable reflections so lit windows and lamps smear vertically down the
 * road. Those vertical smears are the defining signature of wet-night street photography."*
 *
 * A full planar reflection pass would re-render the scene and blow the 20% frame-time budget. But
 * the smear is almost entirely made of the BRIGHT SOURCES, and those are already enumerated -- so
 * this draws one elongated additive quad per source, lying on the ground, stretched along the
 * ground-projected view vector. That is the same construction the real effect has: a mirror image
 * of the source, smeared toward the viewer by surface ripple.
 *
 * Orientation is computed in the vertex shader from the camera position, so the CPU does nothing
 * per frame and the whole set is one draw call.
 */
export function makeWetSmears(sources, groundY = 0.17) {
  // groundY 0.03 put every smear BELOW the 0.15 m kerbed sidewalk it was supposed to lie on, so the
  // whole effect was buried under the pavement and invisible. Sits just proud of the highest ground
  // surface instead; additive with depthWrite off, so a few cm of float costs nothing.
  const usable = sources.filter((s) => s.pos[1] > 1.2);
  const n = usable.length;
  const g = new THREE.BufferGeometry();

  // Four verts per source; corner index carried as an attribute so the shader can build the quad.
  const pos = new Float32Array(n * 4 * 3);
  const corner = new Float32Array(n * 4 * 2);
  const col = new Float32Array(n * 4 * 3);
  const meta = new Float32Array(n * 4 * 2);   // (source height, gain)
  const idx = [];

  usable.forEach((s, i) => {
    const c = new THREE.Color(s.color);
    const corners = [[-1, 0], [1, 0], [1, 1], [-1, 1]];
    for (let k = 0; k < 4; k++) {
      const j = i * 4 + k;
      pos[j * 3] = s.pos[0]; pos[j * 3 + 1] = groundY; pos[j * 3 + 2] = s.pos[2];
      corner[j * 2] = corners[k][0]; corner[j * 2 + 1] = corners[k][1];
      col[j * 3] = c.r; col[j * 3 + 1] = c.g; col[j * 3 + 2] = c.b;
      meta[j * 2] = s.pos[1]; meta[j * 2 + 1] = s.gain || 1;
    }
    idx.push(i * 4, i * 4 + 1, i * 4 + 2, i * 4, i * 4 + 2, i * 4 + 3);
  });

  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('aCorner', new THREE.BufferAttribute(corner, 2));
  g.setAttribute('aColor', new THREE.BufferAttribute(col, 3));
  g.setAttribute('aMeta', new THREE.BufferAttribute(meta, 2));
  g.setIndex(idx);
  g.computeBoundingSphere();

  const mat = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    uniforms: {
      uCam: { value: new THREE.Vector3() },
      uTime: { value: 0 },
      uWet: { value: 1.0 },
      uWidth: { value: 0.85 },
    },
    vertexShader: /* glsl */`
      attribute vec2 aCorner; attribute vec3 aColor; attribute vec2 aMeta;
      uniform vec3 uCam; uniform float uWidth;
      varying vec3 vCol; varying vec2 vC; varying float vAtt;
      void main(){
        vCol = aColor; vC = aCorner;
        vec3 base = position;
        // Ground-projected direction from the source's footpoint toward the camera. The mirror
        // image lies along exactly this line -- that is why the smear always points at you.
        vec3 toCam = uCam - base; toCam.y = 0.0;
        float d = length(toCam);
        vec3 fwd = d > 0.001 ? toCam / d : vec3(0.0, 0.0, 1.0);
        vec3 side = normalize(cross(vec3(0.0, 1.0, 0.0), fwd));
        // Longer smear for a higher source and a nearer viewer: the reflected ray grazes further.
        float len = clamp(aMeta.x * 1.25, 1.2, 14.0) * clamp(6.0 / max(1.5, d), 0.35, 1.6);
        vec3 p = base + side * aCorner.x * uWidth * (0.55 + aCorner.y * 0.9) + fwd * aCorner.y * len;
        vAtt = smoothstep(46.0, 5.0, d) * aMeta.y;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
      }
    `,
    fragmentShader: /* glsl */`
      uniform float uTime; uniform float uWet;
      varying vec3 vCol; varying vec2 vC; varying float vAtt;
      float ws_hash(vec2 p){ return fract(sin(dot(p, vec2(23.7, 91.3))) * 3571.31); }
      void main(){
        // Fade along the smear and across it.
        float along = pow(1.0 - vC.y, 1.7);
        float across = 1.0 - abs(vC.x);
        across = across * across;
        // Break it up: a smear on water is chopped by ripple, never a clean tongue.
        float chop = 0.55 + 0.45 * sin(vC.y * 26.0 + uTime * 3.1 + ws_hash(vec2(vC.x, 0.0)) * 6.0);
        chop *= 0.6 + 0.4 * sin(vC.y * 71.0 - uTime * 5.3);
        float a = along * across * chop * vAtt * uWet * 1.9;
        gl_FragColor = vec4(vCol * a, a);
      }
    `,
  });

  const mesh = new THREE.Mesh(g, mat);
  mesh.name = 'wetSmears';
  mesh.frustumCulled = false;
  mesh.renderOrder = 3;
  return {
    mesh,
    update(t, camPos) {
      mat.uniforms.uTime.value = t;
      mat.uniforms.uCam.value.copy(camPos);
    },
    setWet(v) { mat.uniforms.uWet.value = v; },
  };
}

// ---------------------------------------------------------------------------
// Environment map
// ---------------------------------------------------------------------------
/**
 * Render the city itself into a cube map, once, and use it as the scene environment.
 *
 * This is what makes wet ground read as wet: a puddle has to reflect SOMETHING, and reflecting the
 * actual shopfronts standing over it is both free and correct. An imported HDRI would break the
 * zero-external-asset rule; a flat colour would make every puddle the same.
 */
export function buildEnvironment(renderer, scene, at = new THREE.Vector3(0, 4.5, -6)) {
  const rt = new THREE.WebGLCubeRenderTarget(256, {
    generateMipmaps: true, minFilter: THREE.LinearMipmapLinearFilter,
  });
  const cam = new THREE.CubeCamera(0.5, 260, rt);
  cam.position.copy(at);
  scene.add(cam);
  cam.update(renderer, scene);
  scene.remove(cam);

  const pmrem = new THREE.PMREMGenerator(renderer);
  pmrem.compileCubemapShader();
  const env = pmrem.fromCubemap(rt.texture).texture;
  rt.dispose();
  pmrem.dispose();
  return env;
}

// ---------------------------------------------------------------------------
// Ground wetness
// ---------------------------------------------------------------------------
/**
 * Rain hitting standing water. Injected into the ground materials: animated concentric ripples
 * inside the wet mask, perturbing the normal so the reflection breaks up.
 *
 * Driven off the SAME roughness value the bake produced -- a texel is only rippled where the bake
 * already said there is water, so the ripples cannot appear on dry aggregate.
 */
export const RIPPLE_GLSL = /* glsl */`
uniform float uRainTime;
uniform float uWetness;

float rp_hash(vec2 p){ return fract(sin(dot(p, vec2(41.3, 289.1))) * 24631.7); }

vec3 rippleNormal(vec3 wp, float wet){
  if (wet < 0.02) return vec3(0.0, 0.0, 1.0);
  vec2 base = wp.xz * 1.7;
  vec2 cellf = floor(base);
  float acc = 0.0;
  for (int i = 0; i < 3; i++){
    vec2 o = vec2(float(i) * 0.37, float(i) * 0.71);
    vec2 c = cellf + o;
    float ph = rp_hash(c);
    // Each cell fires a drop on its own phase; the ring expands and dies.
    float t = fract(uRainTime * 1.35 + ph);
    vec2 centre = c + vec2(rp_hash(c + 3.1), rp_hash(c + 7.7));
    float d = length(base - centre);
    float ring = sin((d - t * 1.4) * 34.0) * exp(-d * 3.2) * (1.0 - t) * step(d, t * 1.4);
    acc += ring;
  }
  acc *= wet * uWetness;
  return normalize(vec3(acc * 0.55, acc * 0.55, 1.0));
}
`;

// ---------------------------------------------------------------------------
// Contact shadows
// ---------------------------------------------------------------------------
/**
 * Soft dark patches where objects meet the ground.
 *
 * A critic named their total absence as the single largest tell: *"every object reads as a decal
 * pasted onto the ground plane. A photograph of a night street is mostly occlusion; this set has
 * none, which is why even the good shots feel like a lit diorama."*
 *
 * Real ambient occlusion belongs to post-processing (System 9). This is the cheap correct-enough
 * version: one multiply-blended quad per grounded prop, footprint radius supplied by whatever built
 * the prop, merged into a single draw call.
 */
export function makeContactShadows(grounded) {
  if (!grounded.length) return null;
  // Reported as invisible at r2; the falloff was too soft and the patch too small to read
  // against a wet pavement. Harder core, wider skirt.
  const tex = radialTexture(64, (r) => Math.min(1, Math.max(0, 1 - r) ** 0.85 * 1.35));

  const n = grounded.length;
  const pos = new Float32Array(n * 4 * 3);
  const uv = new Float32Array(n * 4 * 2);
  const idx = [];
  const C = [[-1, -1], [1, -1], [1, 1], [-1, 1]];
  grounded.forEach((gp, i) => {
    for (let k = 0; k < 4; k++) {
      const j = i * 4 + k;
      pos[j * 3] = gp.x + C[k][0] * gp.r * 1.45;
      pos[j * 3 + 1] = gp.y;
      pos[j * 3 + 2] = gp.z + C[k][1] * gp.r * 1.45;
      uv[j * 2] = (C[k][0] + 1) / 2;
      uv[j * 2 + 1] = (C[k][1] + 1) / 2;
    }
    idx.push(i * 4, i * 4 + 1, i * 4 + 2, i * 4, i * 4 + 2, i * 4 + 3);
  });

  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  g.setIndex(idx);
  g.computeBoundingSphere();

  const mat = new THREE.MeshBasicMaterial({
    map: tex,
    transparent: true,
    depthWrite: false,
    blending: THREE.CustomBlending,
    blendSrc: THREE.ZeroFactor,
    blendDst: THREE.OneMinusSrcAlphaFactor,
    polygonOffset: true,
    polygonOffsetFactor: -3,
  });
  const mesh = new THREE.Mesh(g, mat);
  mesh.name = 'contactShadows';
  mesh.renderOrder = 2;
  return mesh;
}
